import { HelpMessage } from "@ids/react-help-message";
import { InputField, type InputFieldProps } from "@ids/react-input-field";
import classNames from "classnames";

import type { CustomRef, InputNecessity } from "@/utils/types";

import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { Popover } from "../popover/Popover";

export type ComposedInputFieldProps = Omit<
  InputFieldProps,
  "value" | "popover"
> & {
  children?: React.ReactNode;
  necessity?: InputNecessity;
  value?: string | number | null;
  style?: React.CSSProperties;
  placeholder?: string;
  popoverHeader?: string | React.ReactNode | null;
  popoverMessage?: string | React.ReactNode | null;
  helpMessage?: string;
  dataTestid?: string;
  errorMessage?: string | false;
} & CustomRef<HTMLInputElement>;

export const ComposedInputField = ({
  id,
  children,
  necessity,
  disabled,
  helpMessage,
  errorMessage,
  className,
  popoverHeader,
  popoverMessage,
  ref,
  value,
  dataTestid,
  onChange,
  ...props
}: ComposedInputFieldProps) => {
  const necessityClass = necessity ?? "";
  const disabledClass = disabled ? "is-disabled" : "";
  const errorMessageId = `${id}-error-message`;
  const helpMessageId = `${id}-help-message`;
  const labelId = `${id}-label`;
  const invalid = !!errorMessage;
  const ariaDescribedby = invalid ? errorMessageId : helpMessageId;

  return (
    <div className={classNames("if flex-column gap-8", className)}>
      <label
        id={labelId}
        className={`if label flex-row align-items-center justify-content-space-between ${necessityClass} ${disabledClass}`}
        htmlFor={id}
      >
        <span>{children}</span>
        <Popover
          popoverHeader={popoverHeader}
          popoverMessage={popoverMessage}
        />
      </label>
      <InputField
        id={id}
        className="if input-field"
        disabled={disabled}
        invalid={invalid}
        required={necessity === "is-required"}
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedby}
        value={value ?? undefined}
        data-testid={dataTestid}
        {...props}
        onChange={onChange}
        ref={ref}
      />
      {!invalid && helpMessage && (
        <HelpMessage
          id={helpMessageId}
          disabled={disabled}
          data-testid={dataTestid ? `${dataTestid}-help-message` : undefined}
        >
          {helpMessage}
        </HelpMessage>
      )}
      {invalid && errorMessage && (
        <ErrorMessage
          id={errorMessageId}
          data-testid={dataTestid ? `${dataTestid}-error-message` : undefined}
        >
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  );
};
